<template>
  <PForm
    nested
    :schema="r$"
    :validate-on="['blur', 'input']"
    class="model-edit-form-purchase-item flex flex-col"
  >
    <div class="primary-lighten-1 flex w-full flex-col gap-y-2 rounded-md p-2">
      <PFormField sr-only label="Artikelnaam" name="name">
        <PInput v-model="purchaseItem.name" ref="inputName" placeholder="Artikelnaam" />
      </PFormField>

      <PFormField sr-only label="Eenheid" name="unit">
        <PInputComboboxUnit v-model="purchaseItem.unit" input-placeholder="Eenheid" />
      </PFormField>

      <PFormField label="Leverancier" sr-only name="supplier">
        <KnownBusinessEntityCombobox
          v-model="purchaseItem.supplier"
          :items="knownBusinessEntities"
          input-placeholder="Leverancier"
          items-key-name="id"
          items-value-name="name"
          creation-option
          grow
          auto-select-newly-created-model
          :creation-model-type="KnownBusinessEntity"
        />
      </PFormField>

      <ModelEditFormProjectPurchaseItem
        v-if="creationParameters?.project"
        :project="creationParameters.project"
        :purchase-item="purchaseItem"
        @input="anyPriceWasChanged = true"
        ref="modelEditFormProjectPurchaseItem"
      />

      <div class="flex items-end gap-x-2">
        <PFormField class="grow" label="Eenheidsprijs">
          <PInputNumber
            v-model="purchaseItem.unitPrice"
            placeholder="Eenheidsprijs"
            allow-zero
            @input="anyPriceWasChanged = true"
          >
            <span class="sm:text-sm">&euro;</span>
          </PInputNumber>
        </PFormField>

        <PButton
          v-if="!_isCreating"
          color="secondary"
          size="sm"
          class="py-1!"
          :icon="['fas', 'gas-pump']"
          :disabled="surchargeIsLoading"
          @click="openSurchargeForm"
        >
          <span class="flex flex-col items-start">
            <span>Brandstoftoeslag</span>
            <span v-if="surchargeDetail" class="text-xs">{{ surchargeDetail }}</span>
          </span>
        </PButton>
      </div>

      <ModelEditFormRetroactivePricing
        v-if="!_isCreating && anyPriceWasChanged"
        v-model="retroactiveDate"
        price-type="purchase"
      >
        <template #subtitle>
          <span class="block text-xs">
            Alle inkopen <b>zonder inkoopfactuurreferentie</b> vanaf deze datum worden aangepast
          </span>
        </template>
      </ModelEditFormRetroactivePricing>

      <PFormField label="Corresponderend verkoopartikel" sr-only>
        <PInputCombobox
          v-model="purchaseItem.saleItem"
          :items="saleItems"
          items-key-name="id"
          :items-value-function="(item: SaleItem) => `${item.name} ${item.getUnitName()}`"
          input-placeholder="Corresponderend verkoopartikel"
          creation-option
          grow
          :creation-model-type="SaleItem"
          @item-created="onSaveSaleItem"
          :creation-parameters="saleItemModelFormCreationParameters"
        />
      </PFormField>
    </div>
    <PAlertFormValidation :model-value="r$" />
  </PForm>
</template>

<script setup lang="ts">
import PForm from "@/components/form/PForm.vue"
import PAlertFormValidation from "@/components/form/PAlertFormValidation.vue"
import { computed, watchEffect, inject, onMounted, ref, useTemplateRef } from "vue"
import { useRegle } from "@regle/core"
import PFormField from "@/components/form/PFormField.vue"
import { storeToRefs } from "pinia"
import { PurchaseItem } from "@/models/purchase-item.model"
import KnownBusinessEntityCombobox from "@/components/known_business_entity/KnownBusinessEntityCombobox.vue"
import PInput from "@/components/form/PInput.vue"
import PInputNumber from "@/components/form/PInputNumber.vue"
import { useSaleItemStore } from "@/store/sale-items.store"
import { SaleItem } from "@/models/sale-item.model"
import { requiredValidator } from "@/helper/validation/validators"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import { PurchaseItemController } from "@/controllers/model-controllers/purchase-item-controller"
import { formFocusHack } from "@/helper/form-helper"
import { upperFirst } from "lodash-es"
import PInputComboboxUnit from "@/components/form/PInputComboboxUnit.vue"
import { Project } from "@/models/project.model"
import ModelEditFormProjectPurchaseItem from "@/components/model_edit_form/ModelEditFormProjectPurchaseItem.vue"
import ModelEditFormRetroactivePricing from "@/components/model_edit_form/ModelEditFormRetroactivePricing.vue"
import type PurchaseController from "@/controllers/model-controllers/purchase-controller"
import { Purchase } from "@/models/purchase.model"
import { uniquePurchaseItemValidator } from "@/helper/validation/purchase-item-validation.ts"
import { useKnownBusinessEntityStore } from "@/store/known-business-entities.store.ts"
import { KnownBusinessEntity } from "@/models/known-business-entity.model.ts"
import PInputCombobox from "@/components/form/PInputCombox.vue"
import PButton from "@/components/elements/PButton.vue"
import usePurchaseSurchargePeriodRetrieval from "@/composables/administration/purchase-surcharge-period-retrieval"
import moment from "moment"

const inputName = ref()

const knownBusinessEntityStore = useKnownBusinessEntityStore()
const saleItemStore = useSaleItemStore()
const { knownBusinessEntities } = storeToRefs(knownBusinessEntityStore)
const { saleItems } = storeToRefs(saleItemStore)

const controller: PurchaseItemController = inject("purchaseItemController")!
const {
  model: purchaseItem,
  initialText,
  isCreating,
  isFormInvalid
} = useFormConfigurator<PurchaseItem>({ controller, customSaveHandler })

// Copy initial value. Else, after creation, the model form appearance will change to edit mode.
const _isCreating = isCreating.value

const creationParameters: { project?: Project; administrativeEvent?: Purchase } =
  inject("creationParameters")!
let saleItemModelFormCreationParameters = creationParameters
if (creationParameters.administrativeEvent) {
  // In case of editing a purchase item based on a purchase processing item, pass down project information of the
  // associated sale.
  saleItemModelFormCreationParameters = {
    project: creationParameters.administrativeEvent.associatedSale?.project
  }
}

onMounted(() => {
  if (initialText) {
    purchaseItem.value.name = upperFirst(initialText)
  }
  formFocusHack(inputName, purchaseItem, "name")
})

const purchaseController: PurchaseController = inject("purchaseController")!
const modelEditFormProjectPurchaseItem = useTemplateRef<typeof ModelEditFormProjectPurchaseItem>(
  "modelEditFormProjectPurchaseItem"
)
async function customSaveHandler(save: () => Promise<void>) {
  await save()
  if (creationParameters.project) {
    await modelEditFormProjectPurchaseItem.value?.savePriceAgreement()
  }
  if (retroactiveDate.value) {
    await purchaseController.applyPricesRetroactively(purchaseItem.value, retroactiveDate.value)
  }
}

function onSaveSaleItem(saleItem: SaleItem) {
  purchaseItem.value.saleItem = saleItem
}

const anyPriceWasChanged = ref(false)
const retroactiveDate = ref()

// Only available when editing an existing purchase item, since the surcharge period is linked to its id.
const {
  surchargeDetail,
  isLoading: surchargeIsLoading,
  openSurchargeForm
} = !_isCreating
  ? usePurchaseSurchargePeriodRetrieval(purchaseItem.value, moment())
  : { surchargeDetail: ref(null), isLoading: ref(false), openSurchargeForm: () => {} }
const rules = {
  supplier: { requiredValidator },
  name: { requiredValidator },
  unit: { requiredValidator },
  $self: { unique: uniquePurchaseItemValidator(purchaseItem) }
}

const { r$ } = useRegle(
  computed(() => ({
    name: purchaseItem.value.name,
    unit: purchaseItem.value.unit,
    supplier: purchaseItem.value.supplier ? true : undefined
  })),
  rules,
  { autoDirty: false }
)

watchEffect(() => {
  isFormInvalid.value = r$.$invalid
})
</script>

<style scoped></style>
